/**
 * Реестр сервисов экосистемы IEC. `id` попадает в `service_id` ledger,
 * в реестр `links` и в SET-события — менять нельзя без миграции.
 */
export type EcosystemService = {
	readonly id: string;
	readonly name: string;
	readonly icon: string;
	readonly url: string;
	/** Есть ли у сервиса собственные платные уровни. */
	readonly hasTiers: boolean;
	/**
	 * Можно ли купить подписку ТОЛЬКО на этот сервис, не беря IEC+ целиком.
	 *
	 * Отдельная подписка даёт право, ограниченное `serviceId` (`entitlements`
	 * это уже умеет), а IEC+ — право с `serviceId = null`, то есть на всё.
	 * Сервис в обоих случаях берёт МАКСИМУМ из своей локальной подписки и
	 * нашего права, поэтому две подписки не конфликтуют: побеждает бо́льшая.
	 */
	readonly standalone: boolean;
};

export const ECOSYSTEM_SERVICES = [
	{
		id: "exchange",
		name: "Exchange!",
		icon: "Plane",
		url: "https://iec.exchange",
		hasTiers: false,
		standalone: false,
	},
	{
		id: "studyhub",
		name: "StudyHub",
		icon: "Book",
		url: "https://study.iec.exchange",
		hasTiers: true,
		standalone: true,
	},
	{
		id: "hero",
		name: "IEC Hero",
		icon: "Smile",
		url: "https://hero.iec.exchange",
		hasTiers: true,
		standalone: true,
	},
	{
		id: "travelhub",
		name: "TravelHub",
		icon: "Globe",
		url: "https://travel.iec.exchange",
		hasTiers: false,
		standalone: false,
	},
	{
		id: "career-lab",
		name: "IEC Career Lab",
		icon: "UserRound",
		url: "https://int.iec.exchange",
		hasTiers: false,
		standalone: false,
	},
	{
		id: "iec-study",
		name: "iec.study",
		icon: "GraduationCap",
		url: "https://iec.study",
		hasTiers: false,
		standalone: false,
	},
	{
		// FluentGo — разговорная практика языка. Живёт под `iec.study`, но это
		// отдельный сервис со своей подпиской и своей единицей расхода
		// (минуты разговора), а не раздел витрины курсов.
		id: "fluentgo",
		name: "FluentGo",
		icon: "MessageCircle",
		url: "https://iec.study/fluent",
		hasTiers: true,
		standalone: true,
	},
	{
		id: "workandtravel",
		name: "Work and Travel",
		icon: "Plane",
		url: "https://workandtravel.com",
		hasTiers: false,
		standalone: false,
	},
	{
		id: "iec-id",
		name: "IEC ID",
		icon: "Sparkles",
		url: "https://id.iec.exchange",
		hasTiers: false,
		standalone: false,
	},
] as const satisfies readonly EcosystemService[];

export type EcosystemServiceId = (typeof ECOSYSTEM_SERVICES)[number]["id"];

/**
 * Адрес витрины IEC ID. Берётся из реестра, а не пишется строкой: и админка, и
 * фронт входа уводят человека «домой» — если бы адрес существовал в двух
 * местах, одно из них однажды осталось бы на старом домене.
 */
export const ID_WEB_URL: string =
	ECOSYSTEM_SERVICES.find((service) => service.id === "iec-id")?.url ?? "https://id.iec.exchange";

export function findService(id: string): EcosystemService | undefined {
	return ECOSYSTEM_SERVICES.find((service) => service.id === id);
}

/** Сервисы, на которые можно подписаться отдельно от IEC+. */
export const STANDALONE_SERVICES = ECOSYSTEM_SERVICES.filter((service) => service.standalone).map(
	(service) => service.id,
);
